import type { MutableRefObject } from 'react';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { BackgroundSize, ImageFittingMode } from './types';

type ElementDimensions = {
  width: number;
  height: number;
};

const DEFAULT_ELEMENT_DIMENSIONS = { height: 0, width: 0 };

type CalculateBackgroundSizeProps = {
  imageFittingMode: ImageFittingMode;
  imageDimensions: ElementDimensions;
  backgroundDimensions: ElementDimensions;
};

function calculateBackgroundSize({
  imageFittingMode,
  imageDimensions,
  backgroundDimensions
}: CalculateBackgroundSizeProps): BackgroundSize {
  if (imageFittingMode === 'fill') {
    return 'cover';
  }

  if (
    imageDimensions.height > backgroundDimensions.height ||
    imageDimensions.width > backgroundDimensions.width
  ) {
    return 'contain';
  }

  return 'unset';
}

type UseBackgroundSizeProps = {
  targetRef: MutableRefObject<HTMLDivElement | null> | null;
  imageFittingMode: ImageFittingMode;
  imageDimensions: ElementDimensions;
};

export function useBackgroundSize({
  targetRef,
  imageFittingMode,
  imageDimensions = DEFAULT_ELEMENT_DIMENSIONS
}: UseBackgroundSizeProps): BackgroundSize {
  const [backgroundDimensions, setBackgroundDimensions] = useState(
    DEFAULT_ELEMENT_DIMENSIONS
  );
  const backgroundSize = useMemo<BackgroundSize>(
    () =>
      calculateBackgroundSize({
        imageFittingMode,
        imageDimensions,
        backgroundDimensions
      }),
    [imageFittingMode, imageDimensions, backgroundDimensions]
  );
  const observer = useRef<ResizeObserver | null>(null);
  const disconnect = useCallback(() => observer.current?.disconnect(), []);

  const observe = useCallback(() => {
    observer.current = new ResizeObserver(([{ contentRect }]) =>
      setBackgroundDimensions({
        width: contentRect.width,
        height: contentRect.height
      })
    );

    if (targetRef?.current) {
      observer.current.observe(targetRef.current);
    }
  }, [targetRef?.current]);

  useLayoutEffect(() => {
    observe();
    return () => disconnect();
  }, [disconnect, observe]);

  return backgroundSize;
}
